import { isObject } from 'lodash'

import LexicalRenderer from 'components/shared/lexical-editor/lexical-renderer'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import { isHtml } from 'utils/CommonUtils'
import { getCurrentLanguage } from 'utils/LanguageUtils'
import { cn } from 'utils/StyleUtils'

import { Button } from '../button'

const EditIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      clipRule="evenodd"
      d="M19.875 3.00368C19.5776 3.00368 19.2924 3.12182 19.0821 3.3321L10.0691 12.3461C9.95056 12.4644 9.86298 12.6114 9.81567 12.7721C9.81561 12.7723 9.81573 12.7719 9.81567 12.7721L9.23217 14.7678L11.2284 14.1842C11.3895 14.1371 11.5364 14.0502 11.6552 13.9316C11.6551 13.9316 11.6552 13.9316 11.6552 13.9316L20.6679 4.91793C20.8781 4.70764 20.9963 4.42239 20.9963 4.125C20.9963 3.82761 20.8782 3.54239 20.6679 3.3321C20.4576 3.12182 20.1724 3.00368 19.875 3.00368ZM17.6679 1.91789C18.2533 1.33253 19.0472 1.00368 19.875 1.00368C20.7028 1.00368 21.4967 1.33253 22.0821 1.91789C22.6675 2.50325 22.9963 3.29717 22.9963 4.125C22.9963 4.95282 22.6675 5.74674 22.0821 6.3321L13.0691 15.3461C12.713 15.7019 12.2726 15.9628 11.7893 16.1039L8.91663 16.9438C8.65848 17.0191 8.38421 17.0238 8.12373 16.9571C7.86325 16.8903 7.62549 16.7548 7.43534 16.5647C7.24519 16.3745 7.10966 16.1368 7.04293 15.8763C6.97619 15.6158 6.98071 15.3421 7.056 15.084L7.8965 12.2093C8.03825 11.7265 8.29914 11.2871 8.65517 10.9316C8.65507 10.9317 8.65528 10.9315 8.65517 10.9316L17.6679 1.91789ZM2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44771 13 3C13 3.55228 12.5523 4 12 4H5C4.73478 4 4.48043 4.10535 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V19C22 19.7956 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7956 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
)

type DisplayFieldProps = {
  className?: string
  label: string
  value: string
  editable?: boolean
  onEdit?: (value: string) => void
}

const DisplayField = ({
  label,
  value,
  className,
  editable,
  onEdit
}: DisplayFieldProps) => {
  const { t } = useEcpTranslation()

  const displayValue = isObject(value)
    ? value[getCurrentLanguage()] || ''
    : value

  return (
    <div
      className={cn(
        'relative flex min-h-12 w-full justify-between gap-2 rounded-2xl bg-base-gray-soft p-4',
        className
      )}
    >
      <div
        className={cn('flex w-full flex-col gap-y-2', {
          'w-4/5 ': editable
        })}
      >
        <p className="text-sm font-medium leading-5 text-gray-light [overflow-wrap:break-word]">
          {label}
        </p>
        {isHtml(displayValue) ? (
          <LexicalRenderer
            className="min-h-5 w-full text-base leading-5 text-gray"
            content={displayValue}
          />
        ) : (
          <p
            className="min-h-5 w-full truncate whitespace-pre-wrap text-base tabular-nums leading-5 text-gray transition-colors"
            title={displayValue}
          >
            {displayValue}
          </p>
        )}
      </div>

      {editable && (
        <Button
          aria-label={`${t('dashboard.edit')} ${label}`}
          onClick={() => {
            onEdit?.(displayValue)
          }}
          variant="icon"
        >
          <EditIcon className="size-6" role="presentation" />
        </Button>
      )}
    </div>
  )
}

export const DisplayFieldSkeleton = () => {
  return (
    <div className="relative flex h-20 w-full flex-col gap-y-2 rounded-2xl bg-base-gray-soft p-4 opacity-80">
      <p className="h-7 w-1/2 max-w-52 animate-pulse rounded-md bg-base-gray-skeleton text-base font-medium leading-5 text-gray-light" />
      <p className="flex h-8 w-3/4 max-w-72 animate-pulse items-center whitespace-pre-wrap rounded-md bg-base-gray-skeleton text-base leading-5 text-gray transition-colors" />
    </div>
  )
}

export default DisplayField
